import React, {useCallback, useEffect, useState} from "react";
import {
  collection,
  getDocs,
  query,
  where,
  getDoc, doc,
} from "firebase/firestore";
import { db } from "../firebase-config";

import { Box, CircularProgress } from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { useSnackbar } from "notistack";

export default function CutByEmployee({ employeeID, claims }) {
  const [tableData, setTableData] = useState([]);
  const apiRef = useGridApiRef();
  const [listLoading, setListLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  const loadDataGrid = useCallback(async () => {
    setListLoading(true);
    try {
      const individualCutsQueryByEmployee = query(
        collection(db, `/accounts/${claims.accountID}/individualCuts`),
        where("employees", "array-contains", employeeID),
        where("cut", "==", true),
      );
      const employeeCutsResults = await getDocs(individualCutsQueryByEmployee);
      let allEmployeeCutsData = [];
      employeeCutsResults.forEach((doc) => {
        allEmployeeCutsData.push({ id: doc.id, ...doc.data()});
      });

      // Fetch every client these cuts refer to in one parallel batch. Awaiting a
      // getDoc per cut meant this grid waited on N serial round-trips, and an
      // employee's whole cut history is unbounded by date.
      const uniqueClientIDs = new Set(allEmployeeCutsData.map((cutData) => cutData.clientID));
      const clientDocs = await Promise.all(
        Array.from(uniqueClientIDs).map((clientID) =>
          getDoc(doc(db, `/accounts/${claims.accountID}/clients/${clientID}`)),
        ),
      );
      const clientDocsMap = {};
      clientDocs.forEach((clientDoc) => {
        clientDocsMap[clientDoc.id] = clientDoc;
      });

      let tempRows = [];
      for (const individualCut of allEmployeeCutsData) {
        const clientDoc = clientDocsMap[individualCut.clientID];
        // Deleting a client does not remove its individualCuts, so this query can
        // return cuts whose client no longer exists. Skip them — reading the missing
        // client data throws and leaves the grid stuck on the loading spinner.
        if (clientDoc === undefined || clientDoc.exists() === false) continue;
        const clientData = clientDoc.data();
        const data = {
          id: individualCut.id,
          firstName: clientData.firstName,
          lastName: clientData.lastName,
          group: individualCut.group,
          cutDate: new Date(individualCut.cutDate.seconds * 1000).toLocaleDateString("en-US"),
          phone: clientData.phone,
          email: clientData.email,
          address: clientData.address || "",
          created: new Date(individualCut.created.seconds * 1000).toLocaleDateString("en-US"),
        };
        tempRows.push(data);
      }
      setTableData([...tempRows]);
    } catch (e) {
      // Without this the spinner never clears and the employee record looks hung.
      console.warn("CutByEmployee loadDataGrid", e);
      enqueueSnackbar("Could not load this employee's completed cuts.", {
        variant: "error",
      });
    } finally {
      setListLoading(false);
    }
  }, [employeeID, claims.accountID, enqueueSnackbar]);

  useEffect( () => {
    if (employeeID && claims?.accountID) {
      void loadDataGrid();
    } else {
      setListLoading(false);
    }
    // Cleanup:
    return () => {
      setListLoading(true);
      setTableData([])
    };
  }, [loadDataGrid, claims.accountID, employeeID]);



const columns = [
    { field: "cutDate", headerName: "Cut Date", width: 100, editable: false },
    { field: "address", headerName: "Address", width: 225, editable: false},
    { field: "firstName", headerName: "First Name", width: 125, editable: false, },
    { field: "lastName", headerName: "Last Name", width: 125, editable: false },
  ];

  return (
    <Box
      sx={{
        p: 3,
      }}
    >
      <Box
        sx={{
          height: 600,
          maxWidth: "600px",
          mx: "auto",
          my: "15px",
        }}
      >
        {!listLoading ? (
          <DataGridPro
            apiRef={apiRef}
            editMode="row"
            rows={tableData}
            columns={columns}
          />
        ) : (
          <CircularProgress />
        )}
      </Box>
    </Box>
  );
}
